// src/services/cozeIntegration.ts
// ============================================
// 扣子(Coze) API 集成模块
//
// 功能：
//   1. 调用扣子 Bot/工作流 执行 AI 任务
//   2. 管理扣子 Bot 列表与配置
//   3. 与 XEAOS 工作流系统集成
//
// 扣子 API 文档: https://www.coze.cn/docs/developer_guides/coze_api_overview
// ============================================

// ============================================
// 类型定义
// ============================================

export interface CozeBot {
  bot_id: string;
  name: string;
  description: string;
  icon_url?: string;
  space_id?: string;
  category: "chat" | "workflow" | "agent" | "plugin";
  status: "active" | "disabled";
  connectedAt: string;
  lastUsedAt?: string;
  callCount: number;
}

export interface CozeChatRequest {
  bot_id: string;
  user_id: string;
  query: string;
  stream?: boolean;
  conversation_id?: string;
  custom_variables?: Record<string, string>;
}

export interface CozeChatResponse {
  code: number;
  msg: string;
  data: {
    conversation_id: string;
    messages: Array<{
      role: string;
      type: string;
      content: string;
      content_type: string;
    }>;
    section_id?: string;
  };
}

export interface CozeWorkflowRequest {
  workflow_id: string;
  parameters: Record<string, any>;
}

export interface CozeWorkflowResponse {
  code: number;
  msg: string;
  data: string; // workflow 执行结果（JSON 字符串）
  debug_url?: string;
}

// ============================================
// 扣子集成管理器
// ============================================

class CozeIntegration {
  private apiKey: string = "";
  private baseUrl: string = "https://api.coze.cn";
  private bots: CozeBot[] = [];
  private listeners: Set<() => void> = new Set();

  constructor() {
    this.loadConfig();
  }

  // ============================================
  // 配置管理
  // ============================================

  private loadConfig() {
    try {
      const stored = localStorage.getItem("xeaos_coze_config");
      if (stored) {
        const config = JSON.parse(stored);
        this.apiKey = config.apiKey || "";
        this.baseUrl = config.baseUrl || "https://api.coze.cn";
        this.bots = config.bots || [];
      }
    } catch {
      // 首次使用，无配置
    }
  }

  private saveConfig() {
    const config = {
      apiKey: this.apiKey,
      baseUrl: this.baseUrl,
      bots: this.bots,
    };
    localStorage.setItem("xeaos_coze_config", JSON.stringify(config));
    this.notifyListeners();
  }

  private notifyListeners() {
    this.listeners.forEach((fn) => fn());
  }

  subscribe(fn: () => void): () => void {
    this.listeners.add(fn);
    return () => this.listeners.delete(fn);
  }

  // ============================================
  // API Key 管理
  // ============================================

  setApiKey(key: string, baseUrl?: string) {
    this.apiKey = key.trim();
    if (baseUrl) this.baseUrl = baseUrl.trim();
    this.saveConfig();
  }

  getApiKey(): string {
    return this.apiKey;
  }

  isConfigured(): boolean {
    return this.apiKey.length > 0;
  }

  getBaseUrl(): string {
    return this.baseUrl;
  }

  // ============================================
  // Bot 管理
  // ============================================

  getBots(): CozeBot[] {
    return [...this.bots];
  }

  getBotsByCategory(category: CozeBot["category"]): CozeBot[] {
    return this.bots.filter((b) => b.category === category && b.status === "active");
  }

  addBot(bot: Omit<CozeBot, "connectedAt" | "callCount">): void {
    const newBot: CozeBot = {
      ...bot,
      connectedAt: new Date().toISOString(),
      callCount: 0,
    };
    this.bots.push(newBot);
    this.saveConfig();
  }

  removeBot(botId: string): void {
    this.bots = this.bots.filter((b) => b.bot_id !== botId);
    this.saveConfig();
  }

  updateBot(botId: string, updates: Partial<CozeBot>): void {
    this.bots = this.bots.map((b) =>
      b.bot_id === botId ? { ...b, ...updates } : b
    );
    this.saveConfig();
  }

  private recordUsage(botId: string): void {
    this.bots = this.bots.map((b) =>
      b.bot_id === botId
        ? { ...b, callCount: b.callCount + 1, lastUsedAt: new Date().toISOString() }
        : b
    );
    this.saveConfig();
  }

  // ============================================
  // API 调用：对话
  // ============================================

  async chat(request: CozeChatRequest): Promise<CozeChatResponse> {
    if (!this.isConfigured()) {
      throw new Error("扣子 API Key 未配置，请先在设置中心配置");
    }

    const response = await fetch(`${this.baseUrl}/v3/chat`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "Authorization": `Bearer ${this.apiKey}`,
      },
      body: JSON.stringify({
        bot_id: request.bot_id,
        user_id: request.user_id,
        stream: false,
        additional_messages: [
          {
            role: "user",
            content: request.query,
            content_type: "text",
          },
        ],
        custom_variables: request.custom_variables || {},
        auto_save_history: true,
      }),
    });

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`扣子 API 调用失败 (${response.status}): ${errorText}`);
    }

    const result = await response.json();
    this.recordUsage(request.bot_id);
    return result;
  }

  // ============================================
  // API 调用：工作流
  // ============================================

  async runWorkflow(request: CozeWorkflowRequest): Promise<CozeWorkflowResponse> {
    if (!this.isConfigured()) {
      throw new Error("扣子 API Key 未配置，请先在设置中心配置");
    }

    const response = await fetch(`${this.baseUrl}/v1/workflow/run`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "Authorization": `Bearer ${this.apiKey}`,
      },
      body: JSON.stringify({
        workflow_id: request.workflow_id,
        parameters: request.parameters,
      }),
    });

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`扣子工作流执行失败 (${response.status}): ${errorText}`);
    }

    const result = await response.json();
    return result;
  }

  // ============================================
  // 便捷方法：快速对话
  // ============================================

  async quickChat(botId: string, query: string, userId: string = "xeaos_user"): Promise<string> {
    const response = await this.chat({
      bot_id: botId,
      user_id: userId,
      query,
    });

    // 提取最后一条 assistant 回复
    const assistantMessages = response.data?.messages?.filter(
      (m) => m.role === "assistant" && m.type === "answer"
    );

    if (assistantMessages && assistantMessages.length > 0) {
      return assistantMessages[assistantMessages.length - 1].content;
    }

    return response.data?.messages?.[response.data.messages.length - 1]?.content || "";
  }

  // ============================================
  // 与 XEAOS 集成：将扣子 Bot 注册为工作流节点
  // ============================================

  /**
   * 将扣子 Bot 封装为 XEAOS 可调用的工作流步骤
   * 在 WorkflowRuntime 中可以作为执行节点
   */
  toWorkflowNode(bot: CozeBot) {
    return {
      id: `coze-${bot.bot_id}`,
      name: bot.name,
      type: "coze_bot" as const,
      description: bot.description,
      config: {
        bot_id: bot.bot_id,
        category: bot.category,
      },
      execute: async (input: { query: string; userId?: string }) => {
        try {
          const result = await this.quickChat(
            bot.bot_id,
            input.query,
            input.userId || "xeaos_workflow"
          );
          return { success: true, data: result };
        } catch (err) {
          return {
            success: false,
            error: err instanceof Error ? err.message : String(err),
          };
        }
      },
    };
  }
}

// ============================================
// 导出单例
// ============================================

export const cozeIntegration = new CozeIntegration();
export default cozeIntegration;
